<template>
	<!-- Skip Link for Keyboard Navigation — teleported before v-main for first-focus order -->
	<Teleport to="#skip-links-portal">
		<a href="#main-content" class="skip-link" @click.prevent="() => { const el = document.getElementById('main-content'); if (el) el.focus(); }">Skip to main content</a>
	</Teleport>
    <div id="main-content" tabindex="-1" role="main" aria-label="Marketing Assets page content">
        <v-row justify="center" class="bg-white">
            <v-col cols="3" class="bg-color-teal-900">
                <v-row class="pa-10 h-screen" justify="center">
                    <div class="text-white">
                        <h1>
                            <LanguagePlaceholder is-html
                                default-text="​​​​​RISE <br/> Marketing Assets" />
                        </h1>
                        <br/>
                         <!-- Language Dropdown -->
                        <label for="language-select" id="language-label">
                            <LanguagePlaceholder default-text="Select Asset Language:" />
                        </label>
                        <div class="language-dropdown-container">
                            <v-select
                                v-model="selectedLanguage"
                                :items="languages"
                                item-title="language"
                                item-value="iso"
                                variant="outlined"
                                density="comfortable"
                                class="language-select"
                                color="black"
                                bg-color="rgba(255, 255, 255, 0.1)"
                                hide-details
                                aria-label="Select language for marketing assets"
                                aria-describedby="language-label"
                                role="combobox"
                                tabindex="0"
                                @update:model-value="onLanguageChange"
                                @keydown.enter="onLanguageChange"
                                @keydown.space="onLanguageChange"
                            >
                                <template v-slot:prepend-inner>
                                    <v-icon color="white" class="mr-2" aria-hidden="true">mdi-translate</v-icon>
                                </template>
                            </v-select>
                        </div>
                        <br/>
                        <h4 class="general-heading">
                            <LanguagePlaceholder is-html        
                                default-text="​​​​​Incentive Period" />
                        </h4>
                        <h4>
                            <LanguagePlaceholder is-html
                                default-text="31 October 2026 – 29 January 2027" />
                        </h4>
                        <br/>
                        <br/>
                        <p>
                            <LanguagePlaceholder default-text="A comprehensive range of marketing assets is available for you to promote the RISE Incentive program to your partners." />
                        </p>
                        <br>
                        <p>
                            <LanguagePlaceholder default-text="Using these marketing assets to support your partners through their RISE Incentive Program journey will encourage them to increase their purchase volumes." />
                        </p>
                        <br>
                        </br>
                    </div>
                </v-row>
            </v-col>
            <v-col cols="8">
                <MarketingAssetsSection :assetCategories="assetCategories"></MarketingAssetsSection>
            </v-col>
            <v-col cols="1"></v-col>
        </v-row>
    </div>

</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'
import MarketingAssetsSection from '@/components/MarketingAssetsSection.vue';
import { useSystemStore } from '@/stores/system'

const systemStore = useSystemStore()
// Language dropdown state
const selectedLanguage = ref('EN') // Set default to EN
const languages = ref([])

// Create functions to generate asset categories based on user type
const getDistributorCategories = (language) => [
    marketingMaterialsToPromoteRise(language),
    // distributorResourcesToDriveRisePartnerActivation(language),
    encouragingPartnersToJoinRise(language)
]

const getDellMarketingCategories = (language) => [
    marketingMaterialsToPromoteRise(language),
    encouragingPartnersToJoinRise(language),
    // dellResourcesToDriveRisePartnerActivation(language),
]

const getTICAdminCategories = (language) => [
    marketingMaterialsToPromoteRise(language),
    //distributorResourcesToDriveRisePartnerActivation(language),
    encouragingPartnersToJoinRise(language),
    //dellResourcesToDriveRisePartnerActivation(language),
]

// Create a computed property for asset categories that automatically updates with user type and language changes
const assetCategories = computed(() => {
    const userType = systemStore.userDetail?.userType?.trim()
    const language = selectedLanguage.value
    
    let categories = []
    
    if (userType === 'Distributor') {
        categories = getDistributorCategories(language)
    } else if (userType === 'Dell Marketing' || userType === 'TP' || userType === 'PAM') {
        categories = getDellMarketingCategories(language)
    } else if (userType === 'TIC_Admin') {
        categories = getTICAdminCategories(language)
    }
    
    // Filter cardContents and actions within each category by userType.
    // If a cardContent or action has no userTypes array it is visible to all user types.
    return categories.map(category => ({
        ...category,
        cardContents: category.cardContents
            .filter(card => !card.userTypes || card.userTypes.includes(userType))
            .map(card => ({
                ...card,
                actions: card.actions.filter(action => !action.userTypes || action.userTypes.includes(userType))
            }))
    }))
})

const encouragingPartnersToJoinRise = (language) => ({
            title: 'Encouraging Partners to join RISE',
            subtitle: '',
            cardContents: [
                // {
                //     title: 'Welcome to RISE video',
                //     subtitle: 'Discover The RISE Incentive Program, designed to reward performance, track achievements, and empower partners to grow their business, unlock exciting rewards, and celebrate success every step of the way, watch this video to see how they can start!',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PartnerIntroduction.png',
                //     actions: [
                //         // {
                //         //      title:'Email Template',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         // {
                //         //     title:'HTML',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         {
                //             title:'Media',
                //             hasPreview: true,
                //             hasEmbed: true,
                //             embedCode: `<img src="https://www.dellmarketing.co.za/RISE/Videos/Q3/WelcomeToRISE/Welcome-to-RISE-${language}.mp4">`,
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://www.dellmarketing.co.za/RISE/Videos/Q3/WelcomeToRISE/Welcome-to-RISE-${language}.mp4`
                //         }     
                //     ]
                // },
                {
                    title: 'Welcome Bonus',
                    subtitle: 'As a new RISE member, Partners could receive a Welcome Bonus of up to $500 when completing their first qualifying purchase',

                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/WelcomeBonusBanner.jpg',
                    actions: [
                        // {
                        //      title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        // {
                        //     title:'HTML',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/WelcomeBonus/Welcome_${language}.zip`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/WelcomeBonus/Welcome_${language}.zip`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/WelcomeBonus/Welcome_OFT_${language}.zip`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/WelcomeBonus/Welcome_OFT_${language}.zip`
                        },
                        // {
                        //     title:'Eligible Partners - Avail Q3WK5',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: true,
                        //     hasLink: false,
                        //     userTypes: ['TIC_Admin', 'TP', 'Dell Marketing', 'PAM'],
                        //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/WelcomeBonus/Data/WelcomeBonus_${language}.xlsx`
                        // } 
                    ]
                },
                // {
                //     title: 'Partner Introduction to RISE',
                //     subtitle: 'Need a simple way to explain the RISE Incentive Program? This one-page overview is perfect for sharing with Dell Technologies Authorized Tier partners.',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PartnerIntroduction.png',
                //     actions: [
                //         // {
                //         //     title:'Email Template',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         {
                //             title:'PDF',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductionToRise/PartnerIntroToRise_${language}.pdf`
                //         },
                //         // {
                //         //     title:'PowerPoint',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: true,
                //         //     hasLink: false,
                //         //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductionToRise/PartnerIntroToRise_PPT_${language}.pptx`
                //         // }     
                //     ]  
                // },
                {
                    title: 'First Purchase Bonus Server +',
                    subtitle: 'The Server+ First Purchase Bonus rewards eligible Partners with $250 when they purchase their first qualifying Server+ unit, provided they have not purchased Server+ products within the previous four quarters.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/FirstPurchaseBonusServer.png',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusServer/OFT_${language}.oft`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusServer/OFT_${language}.oft`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusServer/FirstPurchaseBonusServer_${language}.jpg`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusServer/FirstPurchaseBonusServer_${language}.jpg`
                        },
                        // {
                        //     title:'Eligible Partners - Avail Q3WK5',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: true,
                        //     hasLink: false,
                        //     userTypes: ['TIC_Admin', 'TP', 'Dell Marketing', 'PAM'],
                        //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusServer/EligiblePartners_${language}.xlsx`
                        // }    
                    ]  
                },
                // {
                //     title: 'Partner Introductory Guide',
                //     subtitle: 'This presentation outlines how to best position RISE in conversations with Partners, and highlights why joining this market-leading program is a smart move for their business.',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PartnerIntroduction.png',
                //     actions: [
                //         // {
                //         //     title:'Email Template',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         {
                //             title:'PDF',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductoryGuide/PartnerIntroductoryGuide_${language}.pdf`
                //         },
                //         // {
                //         //     title:'PowerPoint',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: true,
                //         //     hasLink: false,
                //         //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductoryGuide/PartnerIntroductoryGuide_PPT_${language}.pptx`
                //         // }     
                //     ]  
                // },
                {
                    title: 'First Purchase Bonus Storage SSO',
                    subtitle: 'The Storage SSO First Purchase Bonus rewards eligible Partners with $500 when they spend at least $10,000 on qualifying Storage SSO products, provided they have not purchased Storage products within the previous four quarters.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/FirstPurchaseBonusStorageSSO.png',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusStorageSSO/OFT_${language}.oft`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusStorageSSO/OFT_${language}.oft`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusStorageSSO/FirstPurchaseBonusStorageSSO_${language}.jpg`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusStorageSSO/FirstPurchaseBonusStorageSSO_${language}.jpg`
                        },
                        // {
                        //     title:'Eligible Partners - Avail Q3WK5',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: true,
                        //     hasLink: false,
                        //     userTypes: ['TIC_Admin', 'TP', 'Dell Marketing', 'PAM'],
                        //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/FirstPurchaseBonusStorageSSO/EligiblePartners_${language}.xlsx`
                        // }    
                    ]  
                },
                // {
                //     title: 'Distributor Introductory Guide (English Only)',
                //     subtitle: 'This presentation is for your own use. It introduces the RISE Incentive Program and gives you a clear, practical guide on how to encourage your partners to take part, helping you unlock more opportunities to boost your bottom line.',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/DistributorIntroduction.png',
                //     actions: [
                //         // {
                //         //     title:'Email Template',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         {
                //             title:'PDF',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DistributorIntroductoryGuide/DistributorIntroductoryGuide_${language}.pdf`
                //         }
                //         // {
                //         //    title:'Powerpoint',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: true,
                //         //     hasLink: false,
                //         //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DistributorIntroductoryGuide/DistributorIntroductoryGuide_PPT_${language}.pptx`
                //         // }     
                //     ]  
                // }
                {
                    title: 'Dell Pro Max Bonus',
                    subtitle: 'The Dell Pro Max Bonus rewards Partners with an additional $300 for purchasing 8 qualifying units or $600 for 14 units, on top of the standard rewards earned.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/DellProMaxBonus.png',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DellProMaxBonus/OFT_${language}.oft`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DellProMaxBonus/OFT_${language}.oft`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DellProMaxBonus/DellProMaxBonus_${language}.jpg`,
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DellProMaxBonus/DellProMaxBonus_${language}.jpg`
                        },
                        // {
                        //     title:'Eligible Partners - Avail Q3WK5',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: true,
                        //     hasLink: false,
                        //     userTypes: ['TIC_Admin', 'TP', 'Dell Marketing', 'PAM'],
                        //     assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DellProMaxBonus/EligiblePartners_${language}.xlsx`
                        // }    
                    ]  
                },
            ]        
        })

const marketingMaterialsToPromoteRise = (language) => ({
            title: 'Marketing Materials to Promote RISE',
            subtitle: '',
            cardContents: [
                {
                    title: 'Launch OFTs & Social Media Content',
                    subtitle: 'Kickstart your RISE communications with ready-to-use launch templates designed to introduce the program and drive partner engagement.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/LaunchBanner.jpg',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        // {
                        //     title:'HTML',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'Social Media',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/Launch TP/Social Media_${language}.zip`
                        },
                        {
                            title:'RISE Partner OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/Launch TP/${language}.oft`
                        },
                        {
                            title:'RISE Out Partner OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/Launch TP/Register_${language}.oft`
                        }       
                    ],
                              
                },
                {
                    title: 'Redemption OFTs',
                    subtitle: 'Encourage participation and highlight reward opportunities with tailored redemption communication assets.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/RedemptionBanner.jpg',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        // {
                        //     title:'HTML',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/Redemption TP/${language}.OFT`
                        },
                        {
                            title:'PDF',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/Redemption TP/Timeline/Timeline_${language}.pdf`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/Redemption TP/Banner/Banner_${language}.jpg`
                        } 
                    ],
                              
                },
                {
                    title: 'Partner Presentations',
                    subtitle: 'This presentation outlines how to best position RISE in conversations with Partners, and highlights why joining this market-leading program is a smart move for their business.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PartnerPresentationsBanner.png',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        // {
                        //     title:'HTML',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'Partner Introduction To RISE',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://www.dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductionToRise/PartnerIntroToRise_${language}.pdf`
                        },
                        {
                            title:'Partner Introductory Guide',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/PartnerIntroductoryGuide/PartnerIntroductoryGuide_${language}.pdf`
                            
                        }   
                    ],
                              
                },
                {
                    title: 'Distributor Presentations',
                    subtitle: 'This presentation is for your own use. It introduces the RISE Incentive Program and gives you a clear, practical guide on how to encourage your partners to take part, helping you unlock more opportunities to boost your bottom line.',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/DistributorPresentationsBanner.jpg',
                    actions: [
                        // {
                        //     title:'Email Template',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        // {
                        //     title:'HTML',
                        //     hasPreview: false,
                        //     hasEmbed: false,
                        //     embedCode: '',
                        //     hasDownload: false,
                        //     hasLink: false,
                        //     assetURL: ''
                        // },
                        {
                            title:'Distributor Introduction To RISE',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/DistributorPresentation/DistributorIntroToRise_${language}.pdf`
                        },
                        {
                            title:'Distributor Introductory Guide',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/EncouragingPartnersToJoinRise/DistributorIntroductoryGuide/DistributorIntroductoryGuide_${language}.pdf`
                        }  
                    ],
                              
                }
                // {
                //     title: 'Growth Bonus OFTs',
                //     subtitle: 'Promote performance excellence with targeted Growth Bonus templates to inspire and motivate partners.',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/GrowthBonusBanner.png',
                //     actions: [
                //         // {
                //         //     title:'Email Template',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         // {
                //         //     title:'HTML',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         {
                //             title:'OFT',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/GROWTH BONUS/GROWTH_${language}.oft`
                //         },
                //         {
                //             title:'Eligible Partners',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             userTypes: ['TIC_Admin'],
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/GROWTH BONUS/EligiblePartners_${language}.xlsx`
                //         }   
                //     ],
                              
                // },
                // {
                //     title: 'RISE Program Banners',
                //     subtitle: 'Enhance your campaigns with eye-catching RISE banners to maintain consistent program visibility across channels.',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/ProgramBannerQ4FY26.jpg',
                //     actions: [
                //         // {
                //         //     title:'Email Template',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         // {
                //         //     title:'HTML',
                //         //     hasPreview: false,
                //         //     hasEmbed: false,
                //         //     embedCode: '',
                //         //     hasDownload: false,
                //         //     hasLink: false,
                //         //     assetURL: ''
                //         // },
                //         {
                //             title:'Banner',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/MarketingMaterialsToPromoteRise/RiseProgramBanners/Promo banner ${language}.zip`
                //         }     
                //     ],
                              
                // }
            ]        
        })

const distributorResourcesToDriveRisePartnerActivation = (language) => ({
            title: 'Distributor Resources to Drive RISE Partner Activation',
            subtitle: '',
            cardContents: [
                // {
                //     title: 'First Purchase PC',
                //     subtitle: 'Use these asset packs to promote RISE rewards on First Purchase PC purchases. Available this quarter',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PrecisionWorkstation.jpg',
                //     actions: [
                //         {
                //             title:'Email Template / OFT',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/FirstPurchasePC/OFT/PC_${language}.OFT`
                //         },
                //         {
                //             title:'HTML',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/FirstPurchasePC/HTML/PC_${language}.zip`
                //         },
                //         {
                //             title:'Banner',
                //             hasPreview: true,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/FirstPurchasePC/Bannner/First Purchase Bonus Banner ${language}.jpg`
                //         }     
                //     ],
                              
                // },
                // {
                //     title: 'Storage & Server First Purchase Bonus ',
                //     subtitle: 'Use these asset packs to promote RISE rewards on Storage & Server First Purchases. Available this quarter',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/RiseServerPurchase.jpeg',
                //     actions: [
                //         {
                //             title:'Email Template / OFT',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/StorageAndServerFirstPurchaseBonus/OFT/Storage_${language}.OFT`
                //         },
                //         {
                //             title:'HTML',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/StorageAndServerFirstPurchaseBonus/HTML/Storage_${language}.zip`
                //         },
                //         {
                //             title:'Banner',
                //             hasPreview: true,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/StorageAndServerFirstPurchaseBonus/Banner/Storage Server Banner__${language}.jpg`
                //         }     
                //     ],
                              
                // },
                {
                    title: 'Growth Bonus',
                    subtitle: 'Use these asset packs to promote RISE rewards on 2x OptiPlex purchases. Available this quarter',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/GrowthBonusBanner.png',
                    actions: [
                        {
                            title:'Email Template / OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/GROWTH BONUS/GROWTH_${language}.oft`
                        },
                        {
                            title:'HTML',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/GROWTH BONUS/HTML/GROWTH_${language}.zip`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/GROWTH BONUS/Banner/GrowthBonusBanner_${language}.jpg`
                        }     
                    ],
                              
                },
                {
                    title: 'Dell Pro Max Bonus',
                    subtitle: 'Use these asset packs to promote RISE rewards on 3x Latitude purchases. Available this quarter',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PrecisionWorkstationBanner.png',
                    actions: [
                        {
                            title:'Email Template / OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/PrecisionWorkstationBonus/PWS_${language}.oft`
                        },
                        {
                            title:'HTML',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/PrecisionWorkstationBonus/HTML/PWS_${language}.zip`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/PrecisionWorkstationBonus/Banner/DellProMaxBanner_${language}.jpg`
                        }     
                    ],
                              
                }
            ]        
        })

const dellResourcesToDriveRisePartnerActivation = (language) => ({
            title: 'Dell Resources to Drive RISE Partner Activation',
            subtitle: '',
            cardContents: [
                {
                    title: 'First Purchase PC',
                    subtitle: 'Use these asset packs to promote RISE rewards on First Purchase PC purchases. Available this quarter',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/PrecisionWorkstation.jpg',
                    actions: [
                        {
                            title:'Email Template / OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/FirstPurchasePC/OFT/PC_TP_${language}.oft`
                        },
                        {
                            title:'HTML',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/FirstPurchasePC/HTML/PC_TP_${language}.zip`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/FirstPurchasePC/Bannner/First Purchase Bonus Banner ${language}.jpg`
                        }     
                    ],
                              
                },
                {
                    title: 'Storage & Server First Purchase Bonus ',
                    subtitle: 'Use these asset packs to promote RISE rewards on Storage & Server First Purchases. Available this quarter',
                    bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/RiseServerPurchase.jpeg',
                    actions: [
                        {
                            title:'Email Template / OFT',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/StorageAndServerFirstPurchaseBonus/OFT/Storage_TP_${language}.oft`
                        },
                        {
                            title:'HTML',
                            hasPreview: false,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/StorageAndServerFirstPurchaseBonus/HTML/Storage_TP_${language}.zip`
                        },
                        {
                            title:'Banner',
                            hasPreview: true,
                            hasEmbed: false,
                            embedCode: '',
                            hasDownload: true,
                            hasLink: false,
                            assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/StorageAndServerFirstPurchaseBonus/Banner/Storage Server Banner__${language}.jpg`
                        }     
                    ],
                              
                 }
                // {
                //     title: '2x OptiPlex Bonus Assets',
                //     subtitle: `Use ONLY with the account lists that specify which partners are eligible for this promotion. This is NOT a generally available offer. Reach out to your sales leader if you have any questions on this. Use these asset packs to promote RISE rewards on 2x OptiPlex purchases. Available this quarter.`,
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/OptiplexBanner.jpg',
                //     actions: [
                //         {
                //             title:'Email Template / OFT',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/2xOptiPlexBonusAssets/OFT/OptiPlex_TP_${language}.oft`
                //         },
                //         {
                //             title:'HTML',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/2xOptiPlexBonusAssets/HTML/OptiPlex_TP_${language}.zip`
                //         },
                //         {
                //             title:'Banner',
                //             hasPreview: true,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/2xOptiPlexBonusAssets/Bannner/x2 OptiPlex Banner_1300x1400_${language}.jpg`
                //         }     
                //     ],
                              
                // },
                // {
                //     title: '3x Latitude Bonus Assets',
                //     subtitle: ' Use ONLY with the account lists that specify which partners are eligible for this promotion. This is NOT a generally available offer. Reach out to your sales leader if you have any questions on this. Use these asset packs to promote RISE rewards on 3x Latitude purchases. Available this quarter.',
                //     bannerURL: 'https://dellpartnerincentives.com/siteassets/MarketingAssets/BannerImages/LatitudeBanner.jpg',
                //     actions: [
                //         {
                //             title:'Email Template / OFT',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/3xLatitudeBonusAssets/OFT/Latitude_TP_${language}.oft`
                //         },
                //         {
                //             title:'HTML',
                //             hasPreview: false,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/3xLatitudeBonusAssets/HTML/Latitude_TP_${language}.zip`
                //         },
                //         {
                //             title:'Banner',
                //             hasPreview: true,
                //             hasEmbed: false,
                //             embedCode: '',
                //             hasDownload: true,
                //             hasLink: false,
                //             assetURL: `https://dellpartnerincentives.com/siteassets/MarketingAssets/ResourcesToDrivePartnerActivation/3xLatitudeBonusAssets/Bannner/x3 Latitude Banner_1300x400_${language}.jpg`
                //         }     
                //     ],
                              
                // }
            ]        
        })

onMounted(() => {
    // Load available languages
    systemStore.getAllLanguages().then(result => {
        if (result.success && result.languages) {
            languages.value = result.languages
            // Set default language to English if none selected
            if (languages.value.length > 0 && !selectedLanguage.value) {
                selectedLanguage.value = 'EN'
            }
        } else {
            console.log('No languages found in response:', result)
        }
    }).catch(error => {
        console.log('Error loading languages:', error)
        // Fallback to default languages if API fails
        languages.value = [
            { language: 'English', iso: 'EN' },
            { language: 'French', iso: 'FR' },
            { language: 'German', iso: 'DE' },
            { language: 'Italian', iso: 'IT' },
            { language: 'Polish', iso: 'PL' },
            { language: 'Spanish', iso: 'ES' },
            { language: 'Turkish', iso: 'TR' },
            { language: 'Czech', iso: 'CZ' },
            { language: 'Hungarian', iso: 'HU' },
            { language: 'Romanian', iso: 'RO' }
        ]
        if (!selectedLanguage.value) {
            selectedLanguage.value = 'EN'
        }
    })
})

// Language change handler
const onLanguageChange = (languageIso) => {
    console.log('Language changed to:', languageIso)
    
    if (languageIso) {
        // Store the selected language - the computed property will automatically update
        selectedLanguage.value = languageIso
        
        // Show a brief message to confirm language change
        systemStore.showMessage(`Showing assets in ${languageIso}`, { 
            timeout: 2000, 
            color: 'success' 
        })
    }
}



// const assetUrl = ref("siteassets/MarketingAssets/")
                         
// ACQUISITIONS
const distributorPresentationItems = ref([
    { buttonText: 'Distributor Presentation', fileName: 'Acquisitions/Distributor Presentation/Distributor Presentation.pdf', container: 'files' }
])


const intrductionToRisePdfItems = ref([
    { buttonText: 'English', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE EN.pdf', container: 'files' },
    { buttonText: 'French', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE FR.pdf', container: 'files' },
    { buttonText: 'German', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE DE.pdf', container: 'files' },
    { buttonText: 'Italian', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE IT.pdf', container: 'files' },
    { buttonText: 'Polish', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE PL.pdf', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE ES.pdf', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Acquisitions/Introduction to RISE PDF/Introduction to RISE TR.pdf', container: 'files' },

])

const partnerPresentationPdfItems = ref([
    { buttonText: 'English', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation EN.pdf', container: 'files' },
    { buttonText: 'French', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation FR.pdf', container: 'files' },
    { buttonText: 'German', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation DE.pdf', container: 'files' },
    { buttonText: 'Italian', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation IT.pdf', container: 'files' },
    { buttonText: 'Polish', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation PL.pdf', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation ES.pdf', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Acquisitions/Partner Presentation/RISE Partner Presentation TR.pdf', container: 'files' },

])

const intrductionToRiseVideoItems = ref([
    { buttonText: 'English', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 EN.mp4', container: 'files' },
    // { buttonText: 'French', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 FR.mp4', container: 'files' },
    // { buttonText: 'German', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 DE.mp4', container: 'files' },
    // { buttonText: 'Italian', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 IT.mp4', container: 'files' },
    // { buttonText: 'Polish', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 PL.mp4', container: 'files' },
    // { buttonText: 'Spanish', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 ES.mp4', container: 'files' },
    // { buttonText: 'Turkish', fileName: 'Acquisitions/Introduction to RISE video/Introduction to RISE FY25 TR.mp4', container: 'files' },

])

const riseProgramBannerItems = ref([
    { buttonText: 'English', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Acquisitions/RISE Program Banners/RISE FY25 Program Banners TR.zip', container: 'files' },

])

// ONBOARDING

const welcomeToRiseVideoItems = ref([
    { buttonText: 'English', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 EN.mp4', container: 'files' },
    // { buttonText: 'French', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 FR.mp4', container: 'files' },
    // { buttonText: 'German', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 DE.mp4', container: 'files' },
    // { buttonText: 'Italian', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 IT.mp4', container: 'files' },
    // { buttonText: 'Polish', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 PL.mp4', container: 'files' },
    // { buttonText: 'Spanish', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 ES.mp4', container: 'files' },
    // { buttonText: 'Turkish', fileName: 'Onboarding/Welcome to RISE Video/Welcome to RISE FY25 TR.mp4', container: 'files' },

])

// FIRST USE

const distributorPerformanceRedemptionGuideItems = ref([
    { buttonText: 'Distributor Performance and Redemption Guide', fileName: 'First Use/Distributor Performance and Redemption Guide/Distributor Performance & Redemption Guide.pdf', container: 'files' }
])

const partnerRiseRewardRedemptionGuide = ref([
    { buttonText: 'English', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide EN.pdf', container: 'files' },
    { buttonText: 'French', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide FR.pdf', container: 'files' },
    { buttonText: 'German', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide DE.pdf', container: 'files' },
    { buttonText: 'Italian', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide IT.pdf', container: 'files' },
    { buttonText: 'Polish', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide PL.pdf', container: 'files' },
    { buttonText: 'Spanish', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide ES.pdf', container: 'files' },
    { buttonText: 'Turkish', fileName: 'First Use/Partner Reward Redemption Guide/RISE Reward Redemption Guide TR.pdf', container: 'files' },

])

// EXPANSION


const precisionWorkstationBonusAssetPackItems = ref([
    // { buttonText: 'Bulgarian', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack BG.zip', container: 'files' },
    { buttonText: 'Czech', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack CZ.zip', container: 'files' },
    // { buttonText: 'Dutch', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack NL.zip', container: 'files' },
    { buttonText: 'English', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack DE.zip', container: 'files' },
    // { buttonText: 'Greek', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack GR.zip', container: 'files' },
    { buttonText: 'Hungarian', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack HU.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack PL.zip', container: 'files' },
    // { buttonText: 'Portugese', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack PT.zip', container: 'files' },
    { buttonText: 'Romanian', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack RO.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Precision Workstation Bonus Asset Packs/Precision Workstation asset pack TR.zip', container: 'files' },

])

const accountPlays3xLatitudeBonusAssetPackItems = ref([
    { buttonText: 'English', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Account Plays - 3x Latitude Bonus Asset Packs/Latitude Asset Pack TR.zip', container: 'files' },

])


////////////////////////

const accountPlays2xOptiPlexBonusAssetPackItems = ref([
    { buttonText: 'English', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Account Plays - 2x OptiPlex Bonus Asset Packs/OptiPlex Asset Pack TR.zip', container: 'files' },

])

const storageAndServerB2BRewardsAssetPackItems = ref([
    { buttonText: 'English', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Storage and Server B2B Rewards Asset Packs/Storage and Server B2B-TR.zip', container: 'files' },

])

const microsoftWindowsServerRokRewardsAssetPackItems = ref([
    { buttonText: 'English', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Microsoft Windows Server ROK Rewards Asset Packs/ROK Asset Pack TR.zip', container: 'files' },

])

const precisionWorkstationBonusBannerItems = ref([
    { buttonText: 'English', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Precision Workstation Bonus Banners/Precision Bonus Banner TR.zip', container: 'files' },

])

const firstPurchaseBonusBannerItems = ref([
    { buttonText: 'English', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/First Purchase Bonus Banners/First Purchase Bonus Banners TR.zip', container: 'files' },

])

const storageandServerB2BBannerItems = ref([
    { buttonText: 'English', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners EN.zip', container: 'files' },
    { buttonText: 'French', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners FR.zip', container: 'files' },
    { buttonText: 'German', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners DE.zip', container: 'files' },
    { buttonText: 'Italian', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners IT.zip', container: 'files' },
    { buttonText: 'Polish', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners PL.zip', container: 'files' },
    { buttonText: 'Spanish', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners ES.zip', container: 'files' },
    { buttonText: 'Turkish', fileName: 'Expansion/Storage and Server B2B Banners/Storage & Server B2B Banners TR.zip', container: 'files' },

])




</script>

<style scoped>
.language-dropdown-container {
    margin-bottom: 16px;
}

.language-select {
    background-color: rgba(255, 255, 255, 0.1) !important;
}

.language-select :deep(.v-field) {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.language-select :deep(.v-field__input) {
    color: white !important;
}

.language-select :deep(.v-field__label) {
    color: rgba(255, 255, 255, 0.8) !important;
}

.language-select :deep(.v-field__append-inner) {
    color: white !important;
}

.language-select :deep(.v-select__selection) {
    color: white !important;
}

.language-select :deep(.v-list) {
    background-color: #0d2155 !important;
}

.language-select :deep(.v-list-item) {
    color: white !important;
}

.language-select :deep(.v-list-item:hover) {
    background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>